import { Injectable } from '@angular/core';
import { QueryConstraint, where } from '@angular/fire/firestore';
import { Observable, from, map, mergeMap, of } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { Van, VanFirebase, VanIdentification } from '../models/van.model';
import { DbService } from 'src/app/core/services/db.service';
import { toDate, toTimestamp } from 'src/app/shared/utils/firestore-timestamp-utils';

@Injectable({
  providedIn: 'root',
})
export class VanService {
  constructor(private db: DbService) {}

  /**
   * Get all vans owned by a user
   * @param userId The ID of the user
   * @returns Observable of van array
   */
  getUserVans(userId: string): Observable<Van[]> {
    const constraints: QueryConstraint[] = [where('ownerId', '==', userId)];
    return this.db
      .collection$<VanFirebase>('vans', constraints)
      .pipe(map((vans) => vans.map((van) => this.transformFromFirebase(van))));
  }

  /**
   * Get a specific van by ID
   * @param userId The ID of the user (for access control)
   * @param vanId The ID of the van
   * @returns Observable of van or null if not found
   */
  getVan(userId: string, vanId: string): Observable<Van | null> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      map((van) => {
        if (!van) return null;

        // Check if the user has access to this van
        if (van.ownerId !== userId && !van.sharedWith?.includes(userId)) {
          return null; // User doesn't have access
        }

        return this.transformFromFirebase(van);
      })
    );
  }

  /**
   * Create a new van
   * @param userId The ID of the user creating the van
   * @param vanData The van data to create
   * @returns Observable of the created van
   */
  createVan(userId: string, vanData: VanIdentification): Observable<Van> {
    const now = new Date();
    const newVan: Omit<Van, 'id'> = {
      ...vanData,
      ownerId: userId,
      state: 'active',
      stateDate: now,
      createdAt: now,
      sharedWith: [],
    };

    return from(
      this.db.updateAt('vans', this.transformToFirebase(newVan))
    ).pipe(map((result) => ({ ...newVan, id: result.id || '' } as Van)));
  }

  /**
   * Update an existing van
   * @param userId The ID of the user updating the van
   * @param vanId The ID of the van to update
   * @param updates The updates to apply
   * @returns Observable of the updated van
   */
  updateVan(
    userId: string,
    vanId: string,
    updates: Partial<Van>
  ): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to update this van
        if (van.ownerId !== userId) {
          throw new Error('You do not have permission to update this van');
        }

        // If state is being updated, also update stateDate
        const updateData = { ...updates };
        if (updates.state) {
          updateData.stateDate = new Date();
        }

        return from(
          this.db.updateAt(
            `vans/${vanId}`,
            this.transformToFirebase(updateData)
          )
        );
      })
    );
  }

  /**
   * Soft delete a van (mark as deleted)
   * @param userId The ID of the user deleting the van
   * @param vanId The ID of the van to delete
   * @returns Observable of void
   */
  deleteVan(userId: string, vanId: string): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to delete this van
        if (van.ownerId !== userId) {
          throw new Error('You do not have permission to delete this van');
        }

        return from(
          this.db.updateAt(`vans/${vanId}`, {
            state: 'deleted',
            stateDate: toTimestamp(new Date()),
          })
        );
      })
    );
  }

  /**
   * Archive a van
   * @param userId The ID of the user archiving the van
   * @param vanId The ID of the van to archive
   * @returns Observable of void
   */
  archiveVan(userId: string, vanId: string): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to archive this van
        if (van.ownerId !== userId) {
          throw new Error('You do not have permission to archive this van');
        }

        return from(
          this.db.updateAt(`vans/${vanId}`, {
            state: 'archived',
            stateDate: toTimestamp(new Date()),
          })
        );
      })
    );
  }

  /**
   * Restore a van from deleted or archived state
   * @param userId The ID of the user restoring the van
   * @param vanId The ID of the van to restore
   * @returns Observable of void
   */
  restoreVan(userId: string, vanId: string): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to restore this van
        if (van.ownerId !== userId) {
          throw new Error('You do not have permission to restore this van');
        }

        return from(
          this.db.updateAt(`vans/${vanId}`, {
            state: 'active',
            stateDate: toTimestamp(new Date()),
          })
        );
      })
    );
  }

  /**
   * Share a van with another user
   * @param userId The ID of the user sharing the van
   * @param vanId The ID of the van to share
   * @param sharedUserId The ID of the user to share with
   * @returns Observable of void
   */
  shareVan(
    userId: string,
    vanId: string,
    sharedUserId: string
  ): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to share this van
        if (van.ownerId !== userId) {
          throw new Error('You do not have permission to share this van');
        }

        // Add the user to the sharedWith array if not already there
        const sharedWith = van.sharedWith || [];
        if (!sharedWith.includes(sharedUserId)) {
          sharedWith.push(sharedUserId);

          return from(
            this.db.updateAt(`vans/${vanId}`, {
              sharedWith,
            })
          );
        }

        return of({ id: vanId });
      })
    );
  }

  /**
   * Remove a user from the shared list
   * @param userId The ID of the user removing the share
   * @param vanId The ID of the van
   * @param sharedUserId The ID of the user to remove from sharing
   * @returns Observable of void
   */
  unshareVan(
    userId: string,
    vanId: string,
    sharedUserId: string
  ): Observable<{ id?: string }> {
    return this.db.doc$<VanFirebase>(`vans/${vanId}`).pipe(
      mergeMap((van) => {
        if (!van) {
          throw new Error('Van not found');
        }

        // Check if the user has permission to modify sharing
        if (van.ownerId !== userId) {
          throw new Error(
            'You do not have permission to modify sharing for this van'
          );
        }

        // Remove the user from the sharedWith array
        const sharedWith = (van.sharedWith || []).filter(
          (id) => id !== sharedUserId
        );

        return from(
          this.db.updateAt(`vans/${vanId}`, {
            sharedWith,
          })
        );
      })
    );
  }

  /**
   * Transform a van from Firestore format to application format
   * @param firebaseVan The van in Firestore format
   * @returns The van in application format
   */
  private transformFromFirebase(firebaseVan: VanFirebase): Van {
    const result: Van = {
      ...firebaseVan,
      stateDate: toDate(firebaseVan.stateDate),
      createdAt: toDate(firebaseVan.createdAt),
    } as Van;

    // Handle vanniversary if it exists
    if (firebaseVan.vanniversary && firebaseVan.vanniversary.date) {
      result.vanniversary = {
        date: toDate(firebaseVan.vanniversary.date),
      };
    }

    // Handle safety if it exists
    if (firebaseVan.safety && firebaseVan.safety.items) {
      result.safety = {
        items: firebaseVan.safety.items.map((firebaseItem) => ({
          ...firebaseItem,
          id: uuidv4(), // Generate new ID for each item
          expiryDate: toDate(firebaseItem.expiryDate),
        })),
      };
    }

    // Handle howDidYouFindUs if it exists
    if (
      firebaseVan.howDidYouFindUs &&
      firebaseVan.howDidYouFindUs.submittedAt
    ) {
      result.howDidYouFindUs = {
        ...firebaseVan.howDidYouFindUs,
        submittedAt: toDate(firebaseVan.howDidYouFindUs.submittedAt),
      };
    }

    return result;
  }

  /**
   * Transform a van from application format to Firestore format
   * @param van The van in application format
   * @returns The van in Firestore format
   */
  private transformToFirebase(van: Partial<Van>): Partial<VanFirebase> {
    const transformed: Partial<VanFirebase> = {};

    // Only include properties that have defined values
    Object.entries(van).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle date fields
        if (key === 'stateDate' || key === 'createdAt') {
          transformed[key as 'stateDate' | 'createdAt'] = toTimestamp(
            value as Date
          );
        }
        // Handle vanniversary object with date
        else if (key === 'vanniversary' && value && (value as any).date) {
          transformed.vanniversary = {
            date: toTimestamp((value as any).date),
          };
        }
        // Handle safety object with items
        else if (key === 'safety' && value && (value as any).items) {
          transformed.safety = {
            items: (value as any).items.map((item: any) => ({
              ...item,
              expiryDate: toTimestamp(item.expiryDate),
            })),
          };
        }
        // Handle howDidYouFindUs object with submittedAt date
        else if (
          key === 'howDidYouFindUs' &&
          value &&
          (value as any).submittedAt
        ) {
          transformed.howDidYouFindUs = {
            ...(value as any),
            submittedAt: toTimestamp((value as any).submittedAt),
          };
        } else {
          // Use type assertion to handle other fields
          transformed[key as keyof VanFirebase] = value as any;
        }
      }
    });

    return transformed;
  }
}
